"use client";

import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Instagram, Twitter, Facebook, Youtube, Loader, ArrowRight } from "lucide-react";
function FooterNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-4">
        {[1, 2, 3, 4].map(i => <li key={i} className="h-4 w-24 bg-muted animate-pulse rounded-lg" />)}
      </ul>;
  }
  return <ul className="space-y-4">
      {menuItems.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="text-foreground/70 hover:text-accent transition-colors font-default text-sm tracking-wide">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-4">
        <p className="text-accent font-medium text-sm">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-0">
      <div className="flex flex-col sm:flex-row gap-4 items-end">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1 w-full">
              <FormControl>
                <Input type="email" placeholder="Your email address" className="bg-transparent border-0 border-b border-foreground/20 rounded-lg h-12 px-0 focus-visible:ring-0 focus-visible:border-accent transition-all placeholder:text-foreground/30" {...field} />
              </FormControl>
              <FormMessage className="text-[10px] uppercase tracking-tighter" />
            </FormItem>} />
        <Button type="submit" disabled={isSubmitting} className="h-12 px-8 bg-accent text-accent-foreground font-bold uppercase tracking-widest rounded-lg hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all shrink-0">
          {isSubmitting ? <Loader className="animate-spin size-4" /> : <span className="flex items-center gap-2">
              Join <ArrowRight className="size-4" />
            </span>}
        </Button>
      </div>
      {submitError && <p className="text-destructive text-[10px] uppercase mt-2">{submitError}</p>}
    </Form>;
}
export default function Footer() {
  const addressLines = ["6701 Corporate Drive, Suite N", "Johnston, IA 50131", "United States"];
  const supportLinks = [{
    label: "Shipping & Returns",
    href: "/ShippingReturns"
  }, {
    label: "Privacy Policy",
    href: "/PrivacyPolicy"
  }, {
    label: "Terms of Service",
    href: "/TermsOfService"
  }, {
    label: "Contact Us",
    href: "/CustomerCarePage"
  }];
  const socialIcons = [{
    icon: Instagram,
    label: "Instagram",
    href: "#"
  }, {
    icon: Twitter,
    label: "Twitter",
    href: "#"
  }, {
    icon: Facebook,
    label: "Facebook",
    href: "#"
  }, {
    icon: Youtube,
    label: "Youtube",
    href: "#"
  }];
  const paymentMethods = [{
    name: "Visa",
    icon: ""
  }, {
    name: "Mastercard",
    icon: ""
  }, {
    name: "Apple Pay",
    icon: ""
  }, {
    name: "PayPal",
    icon: "🅿"
  }];
  return <footer data-section-id="4474" id="site-footer" className="bg-background text-foreground pt-24 pb-12 px-6 md:px-12 lg:px-16 border-t border-border">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8 mb-16">
        <div className="lg:col-span-2 space-y-6">
          <Link to="/" className="inline-block">
            <WvcLogo className="h-7 w-auto" />
          </Link>
          <div className="text-sm text-foreground/70 space-y-1">
            {addressLines.map((line, i) => <p key={i}>{line}</p>)}
          </div>
          <div className="flex items-center gap-4 pt-2">
            {socialIcons.map((social, i) => {
            const IconComponent = social.icon;
            return <Link key={i} to={social.href} className="text-foreground hover:text-accent transition-colors p-2 bg-secondary rounded-lg">
                  <IconComponent className="size-4" />
                </Link>;
          })}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-sm uppercase tracking-wider">Navigation</h3>
          <MenuProvider menu_id="23">
            <FooterNavConsumer />
          </MenuProvider>
        </div>

        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-sm uppercase tracking-wider">Customer Care</h3>
          <ul className="space-y-3">
            {supportLinks.map((link, i) => <li key={i} data-index={i}>
                <Link to={link.href} className="text-foreground/70 hover:text-accent transition-colors text-sm">
                  {link.label}
                </Link>
              </li>)}
          </ul>
        </div>

        <div className="space-y-4 lg:col-span-1">
          <h3 className="font-heading font-semibold text-sm uppercase tracking-wider">Newsletter</h3>
          <p className="text-sm text-foreground/70">Subscribe to receive updates, access to exclusive deals, and more.</p>
          <FormProvider formId="footer-newsletter" sectionName="footer" defaultValues={{
          email: ""
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          }
        }} successMessage="Thank you for subscribing!">
            <NewsletterForm />
          </FormProvider>
        </div>
      </div>

      <Separator className="my-8" />

      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-xs text-foreground/60">
        <p>&copy; {new Date().getFullYear()} Lush Fashion. All rights reserved.</p>
        <div className="flex items-center gap-3">
          {paymentMethods.map((pm, i) => <span key={i} className="px-3 py-1.5 bg-secondary rounded-lg font-medium text-foreground">
              {pm.icon || pm.name}
            </span>)}
        </div>
      </div>
    </footer>;
}